import {
  DEFAULT_LOCALE,
  getLocaleDirection,
  getLocaleNumberingSystem,
  loadCatalog,
  type ArabicNumerals,
  type Locale,
} from '@syncanix/shared';
import { I18nProvider as UiI18nProvider } from '@syncanix/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { I18nContext, useT, type I18nContextValue } from './i18n-context';

import type { PropsWithChildren, ReactElement } from 'react';

/**
 * `I18nProvider` — AF-10 message-catalog scaffold (iter 387).
 *
 * Owns three responsibilities:
 *
 *   1. **Locale state** — the current `Locale` ('en' | 'es' | … |
 *      'ar' | 'he'). Defaults to `DEFAULT_LOCALE` from `@syncanix/
 *      shared`; mutable via `setLocale` for the locale picker.
 *   2. **`<html lang>` + `<html dir>` wiring** — every locale
 *      change writes to `documentElement` so RTL switching is
 *      one state update away. Per the AF-10 audit findings,
 *      this is the load-bearing piece that unblocks Arabic /
 *      Hebrew testing even before the message catalogs are
 *      translated.
 *   3. **String lookup** — `t(key, params?)` returns the catalog
 *      value with `{param}` placeholders interpolated. Falls
 *      back to the key itself when no entry is found, so
 *      translator gaps don't crash the UI (they just surface
 *      as a developer-visible "common.unknown" string).
 *
 * **Catalog injection** (Architect hat, locked iter 387): the
 * provider takes `messages` as a prop rather than fetching them
 * itself. The app's root composition decides whether to bundle
 * the catalog at build time, load it via fetch at boot, or
 * stack multiple catalogs (active locale + EN fallback). Tests
 * pass a minimal fixture inline. Per Pass-2 v1 plan, only EN
 * has translations; non-EN locales fall back to the key (which
 * surfaces a developer-visible "untranslated string" marker —
 * NOT a runtime error).
 *
 * **Provider absence is loud** (locked iter 387): `useT()`
 * throws when called outside the provider. The web app's root
 * composition MUST include `<I18nProvider>` at the top of the
 * tree; a missing wrapper is a structural bug, not a recoverable
 * runtime condition.
 */

export interface I18nProviderProps {
  /**
   * Legacy static-catalog path. When provided, `t` resolves against this
   * fixed catalog regardless of locale (back-compat for existing call sites
   * and tests). New code should omit it and rely on `catalogFor` instead.
   */
  readonly messages?: Readonly<Record<string, string>>;
  /**
   * SYNC catalog-aware mode (AF-10.M2): resolves the catalog for the active
   * locale reactively on every `setLocale`. Ignored when the legacy `messages`
   * prop is supplied. No default — a sync default would have to statically
   * bundle all six message catalogs (~350 KB gzipped) into the entry chunk,
   * which is exactly the A23 budget breach this provider's async mode fixed
   * (2026-07-02). Tests pass `getCatalog` from
   * `@syncanix/shared/i18n-catalogs` here to stay synchronous.
   */
  readonly catalogFor?: (locale: Locale) => Readonly<Record<string, string>>;
  /**
   * ASYNC catalog mode — the production default (`@syncanix/shared`'s
   * `loadCatalog`): each locale's catalog arrives as its own lazy chunk, so
   * the app entry ships zero catalog bytes. While the FIRST catalog loads the
   * provider renders nothing (the boot splash is still on screen; the chunk
   * is same-origin and cache-friendly, so this is a one-time few-ms gate). On
   * a locale SWITCH the previous catalog stays live until the next one
   * resolves — no key-echo flash. Ignored when `messages` or `catalogFor` is
   * supplied.
   */
  readonly loadCatalogFor?: (locale: Locale) => Promise<Readonly<Record<string, string>>>;
  /** Defaults to `DEFAULT_LOCALE` ('en'). */
  readonly initialLocale?: Locale;
  /**
   * Fired on every explicit `setLocale` call (NOT on mount — an auto-detected
   * initial locale must not be persisted as a deliberate choice). The app uses
   * this to persist the user's choice; the provider stays storage-agnostic.
   */
  readonly onLocaleChange?: (locale: Locale) => void;
  /** Defaults to `'western'` (the locked-spec default — ADR-0141). */
  readonly initialNumerals?: ArabicNumerals;
  /**
   * Fired on every explicit `setNumerals` call (NOT on mount), so the app can
   * persist the choice. The provider stays storage-agnostic, exactly like
   * `onLocaleChange`.
   */
  readonly onNumeralsChange?: (numerals: ArabicNumerals) => void;
}

export function I18nProvider({
  children,
  messages,
  catalogFor,
  loadCatalogFor = loadCatalog,
  initialLocale = DEFAULT_LOCALE,
  onLocaleChange,
  initialNumerals = 'western',
  onNumeralsChange,
}: PropsWithChildren<I18nProviderProps>): ReactElement | null {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const [numerals, setNumeralsState] = useState<ArabicNumerals>(initialNumerals);

  // Async catalog state — only driven when neither sync prop is supplied.
  // `null` means "no catalog resolved yet" (first load only); on a locale
  // switch the previous catalog stays until the next one resolves.
  const asyncMode = messages === undefined && catalogFor === undefined;
  const [asyncCatalog, setAsyncCatalog] = useState<Readonly<Record<string, string>> | null>(null);

  useEffect(() => {
    if (!asyncMode) return;
    let cancelled = false;
    loadCatalogFor(locale).then(
      (catalog) => {
        if (!cancelled) setAsyncCatalog(catalog);
      },
      (error: unknown) => {
        // Fail loud but usable: key-echo (the t() fallback) beats an
        // indefinitely blank app if a catalog chunk ever fails to load.
        console.error('[i18n] failed to load catalog', { locale, error });
        if (!cancelled) setAsyncCatalog({});
      },
    );
    return () => {
      cancelled = true;
    };
  }, [asyncMode, loadCatalogFor, locale]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = locale;
    document.documentElement.dir = getLocaleDirection(locale);
  }, [locale]);

  // Fire `onLocaleChange` only on an EXPLICIT switch, never on mount — so an
  // auto-detected initial locale is not persisted as if the user had chosen
  // it (that would let detection override a later browser-language change).
  // Persistence therefore records real choices only.
  const setLocale = useCallback(
    (next: Locale) => {
      setLocaleState(next);
      onLocaleChange?.(next);
    },
    [onLocaleChange],
  );

  // Same explicit-choice-only persistence contract as `setLocale`.
  const setNumerals = useCallback(
    (next: ArabicNumerals) => {
      setNumeralsState(next);
      onNumeralsChange?.(next);
    },
    [onNumeralsChange],
  );

  const numberingSystem = useMemo(
    () => getLocaleNumberingSystem(locale, numerals),
    [locale, numerals],
  );

  // Legacy static catalog wins for back-compat; then the sync catalogFor
  // path; otherwise the async-loaded catalog (production default).
  const activeCatalog = useMemo(
    () => messages ?? (catalogFor !== undefined ? catalogFor(locale) : asyncCatalog),
    [messages, catalogFor, locale, asyncCatalog],
  );

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const template = activeCatalog?.[key] ?? key;
      return interpolate(template, params, locale, numberingSystem);
    },
    [activeCatalog, locale, numberingSystem],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, numerals, setNumerals, numberingSystem, t }),
    [locale, setLocale, numerals, setNumerals, numberingSystem, t],
  );

  // First-load gate (async mode only): render nothing until the initial
  // catalog resolves. A one-time same-origin chunk fetch (few ms, cached
  // thereafter) — the alternative is a raw-key flash across the entire UI on
  // every boot. Locale SWITCHES never hit this: the previous catalog stays
  // active until the next one resolves.
  if (activeCatalog === null) return null;

  return (
    <I18nContext.Provider value={value}>
      {/*
        Bridge the SAME locale + catalog into @syncanix/ui's own provider:
        ui components translate their internal strings (e.g. the Dialog's
        sr-only close label) through the package-local useT, which key-echoes
        without a provider. Until 2026-06-10 this was never mounted, so every
        ui-internal string read as its raw key to screen readers, app-wide.
      */}
      <UiI18nProvider locale={locale} messages={activeCatalog}>
        {children}
      </UiI18nProvider>
    </I18nContext.Provider>
  );
}

const PARAM_PATTERN = /\{(\w+)\}/g;

function interpolate(
  template: string,
  params?: Record<string, string | number>,
  locale?: string,
  numberingSystem?: string,
): string {
  if (params === undefined) return template;
  return template.replace(PARAM_PATTERN, (match, name: string) => {
    const value = params[name];
    if (value === undefined) return match;
    // Honour an OPT-IN numbering system for RAW numeric params (localization-rtl.md
    // § Numerals: Western `latn` is the default, Eastern Arabic-Indic `arab` is
    // opt-in). Guarded on `!== 'latn'` so the default config stays byte-identical to
    // `String(value)` for every locale, and `useGrouping: false` so a year or id can
    // never gain a thousands separator — interpolate cannot tell a count from an
    // identifier, and a call site that wants grouping pre-formats and passes a
    // STRING, which flows through here untouched.
    if (
      typeof value === 'number' &&
      locale !== undefined &&
      numberingSystem !== undefined &&
      numberingSystem !== 'latn'
    ) {
      return new Intl.NumberFormat(locale, { numberingSystem, useGrouping: false }).format(value);
    }
    return String(value);
  });
}

// Re-export: `TSlots` moved to its own module when the provider grew its
// async-catalog mode past the ~300-line ceiling; existing imports from this
// path keep working.

export { TSlots, type TSlotsProps } from './TSlots';

// The context value + `useT` live in `i18n-context.ts` — the leaf `TSlots.tsx`
// reads, so its import of the hook no longer points back at this module. Both
// are re-exported here because ~100 call sites import them from this path.
//
// The fast-refresh rule's advice ("use a new file to share functions between
// components") is exactly what `i18n-context.ts` IS; this line is the
// compatibility shim over that move, and moving 100 imports to satisfy a
// warning about module shape would be a large diff for no behavioural gain.
// The disable is not new — it sat on `useT`'s own declaration here before the
// hoist, for the same reason.
// eslint-disable-next-line react-refresh/only-export-components -- re-export shim; the hook itself lives in i18n-context.ts
export { useT, type I18nContextValue };
